import { aiProxyClient } from "@/services/aiProxyClient";
import { clickupService } from "@/services/clickupService";
import type { AgentAnalysisResult } from "@/types/communications";
import type { ClickUpListOption, ClickUpTask } from "@/types/clickup";

const MATCH_MODEL = "google/gemini-2.5-flash";

export interface DetectedProject {
  name: string;
  listId?: string;
  confidence: number;
  reason: string;
}

export interface RelatedExistingTask {
  id: string;
  name: string;
  url: string;
  relevance: number;
  reason: string;
}

export interface ProposedDeliverable {
  title: string;
  description?: string;
  owner?: string;
  dueDate?: string;
  priority?: string;
  selected: boolean;
}

export interface ResolvedClickUpMeeting {
  itemId: string;
  itemLabel: string;
  detectedProject: DetectedProject;
  matchedListId: string;
  matchedListName: string;
  relatedExistingTasks: RelatedExistingTask[];
  proposedDeliverables: ProposedDeliverable[];
}

interface DeliverableParsed {
  title?: string;
  description?: string;
  owner?: string;
  dueDate?: string;
  priority?: string;
}

function parseDeliverables(parsed: unknown): DeliverableParsed[] {
  const record = parsed as Record<string, unknown> | undefined;
  if (!record || !Array.isArray(record.deliverables)) return [];
  return record.deliverables.filter(
    (d): d is DeliverableParsed =>
      Boolean(d && typeof d === "object" && typeof (d as DeliverableParsed).title === "string")
  );
}

function parseDetectedProject(parsed: unknown, itemLabel: string): DetectedProject {
  const record = parsed as Record<string, unknown> | undefined;
  const raw = record?.detectedProject as Record<string, unknown> | undefined;
  if (raw && typeof raw.name === "string" && raw.name.trim()) {
    return {
      name: raw.name.trim(),
      listId: typeof raw.listId === "string" ? raw.listId : undefined,
      confidence: typeof raw.confidence === "number" ? raw.confidence : 0.5,
      reason: typeof raw.reason === "string" ? raw.reason : "Detected from meeting content",
    };
  }
  return {
    name: itemLabel,
    confidence: 0.3,
    reason: "No project explicitly mentioned — using meeting title as hint",
  };
}

function normalizeText(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9\s]/g, " ");
}

function scoreListMatch(list: ClickUpListOption, project: DetectedProject, itemLabel: string): number {
  if (project.listId && project.listId === list.id) return 1;

  const haystack = normalizeText(`${list.name} ${list.path}`);
  const needles = [project.name, itemLabel]
    .map(normalizeText)
    .flatMap((s) => s.split(/\s+/).filter((w) => w.length > 2));

  if (needles.length === 0) return 0;
  const hits = needles.filter((n) => haystack.includes(n)).length;
  return hits / needles.length;
}

function scoreTaskRelevance(
  task: ClickUpTask,
  deliverables: DeliverableParsed[]
): { relevance: number; reason: string } {
  const taskText = normalizeText(
    `${task.name} ${task.description ?? ""} ${task.text_content ?? ""}`
  );
  const titles = deliverables.map((d) => normalizeText(d.title ?? "")).filter(Boolean);
  if (titles.length === 0) return { relevance: 0, reason: "No deliverables to compare" };

  let best = 0;
  let bestTitle = "";
  for (const title of titles) {
    const words = title.split(/\s+/).filter((w) => w.length > 2);
    if (words.length === 0) continue;
    const hits = words.filter((w) => taskText.includes(w)).length;
    const score = hits / words.length;
    if (score > best) {
      best = score;
      bestTitle = title;
    }
  }

  if (best >= 0.5) {
    return { relevance: best, reason: `Matches deliverable "${bestTitle}"` };
  }
  if (best > 0) {
    return { relevance: best, reason: `Partial overlap with "${bestTitle}"` };
  }
  return { relevance: 0, reason: "No strong overlap" };
}

async function aiPickList(
  lists: ClickUpListOption[],
  project: DetectedProject,
  itemLabel: string
): Promise<{ listId: string; reason: string } | null> {
  if (lists.length === 0) return null;

  const compactLists = lists.slice(0, 80).map((l) => ({
    id: l.id,
    name: l.name,
    path: l.path,
  }));

  const response = await aiProxyClient.chatCompletions({
    model: MATCH_MODEL,
    temperature: 0.1,
    messages: [
      {
        role: "system",
        content:
          "Pick the best ClickUp list for a meeting. Return JSON only: { listId, reason }",
      },
      {
        role: "user",
        content: JSON.stringify({
          meetingTitle: itemLabel,
          detectedProject: project,
          lists: compactLists,
        }),
      },
    ],
    response_format: { type: "json_object" },
  }, { app: "clickup_resolve" });

  const json = await response.json();
  const content = json?.choices?.[0]?.message?.content;
  if (typeof content !== "string") return null;

  try {
    const parsed = JSON.parse(content) as { listId?: string; reason?: string };
    if (parsed.listId && lists.some((l) => l.id === parsed.listId)) {
      return {
        listId: parsed.listId,
        reason: parsed.reason ?? "AI-matched list",
      };
    }
  } catch {
    return null;
  }
  return null;
}

async function matchList(
  lists: ClickUpListOption[],
  project: DetectedProject,
  itemLabel: string
): Promise<{ listId: string; listName: string; reason: string }> {
  if (project.listId) {
    const exact = lists.find((l) => l.id === project.listId);
    if (exact) {
      return { listId: exact.id, listName: exact.name, reason: project.reason };
    }
  }

  const scored = lists
    .map((list) => ({ list, score: scoreListMatch(list, project, itemLabel) }))
    .sort((a, b) => b.score - a.score);

  if (scored[0] && scored[0].score >= 0.4) {
    return {
      listId: scored[0].list.id,
      listName: scored[0].list.name,
      reason: `Matched "${project.name}" to list "${scored[0].list.name}"`,
    };
  }

  const aiPick = await aiPickList(lists, project, itemLabel);
  if (aiPick) {
    const list = lists.find((l) => l.id === aiPick.listId)!;
    return { listId: list.id, listName: list.name, reason: aiPick.reason };
  }

  const fallback = scored[0]?.list ?? lists[0];
  return {
    listId: fallback.id,
    listName: fallback.name,
    reason: "Defaulted to best available list",
  };
}

export async function resolveClickUpProjectForResult(
  teamId: string,
  lists: ClickUpListOption[],
  result: AgentAnalysisResult
): Promise<ResolvedClickUpMeeting> {
  const deliverables = parseDeliverables(result.parsed);
  const detectedProject = parseDetectedProject(result.parsed, result.itemLabel);
  const { listId, listName, reason } = await matchList(lists, detectedProject, result.itemLabel);

  let relatedExistingTasks: RelatedExistingTask[] = [];
  try {
    const tasks = await clickupService.listTasksInList(teamId, listId, false);
    relatedExistingTasks = tasks
      .map((task) => {
        const { relevance, reason: taskReason } = scoreTaskRelevance(task, deliverables);
        return {
          id: task.id,
          name: task.name,
          url: task.url,
          relevance,
          reason: taskReason,
        };
      })
      .filter((t) => t.relevance > 0)
      .sort((a, b) => b.relevance - a.relevance)
      .slice(0, 8);
  } catch {
    relatedExistingTasks = [];
  }

  return {
    itemId: result.itemId,
    itemLabel: result.itemLabel,
    detectedProject: { ...detectedProject, reason: reason || detectedProject.reason },
    matchedListId: listId,
    matchedListName: listName,
    relatedExistingTasks,
    proposedDeliverables: deliverables.map((d) => ({
      title: d.title ?? "Untitled task",
      description: d.description,
      owner: d.owner,
      dueDate: d.dueDate,
      priority: d.priority,
      selected: true,
    })),
  };
}

export async function resolveClickUpProjectsForResults(
  results: AgentAnalysisResult[]
): Promise<{ teamId: string; meetings: ResolvedClickUpMeeting[] }> {
  const teams = await clickupService.listTeams();
  if (teams.length === 0) {
    throw new Error("No ClickUp teams found. Connect ClickUp first.");
  }

  const teamId = teams[0].id;
  const lists = await clickupService.listLists(teamId);
  if (lists.length === 0) {
    throw new Error("No ClickUp lists found in your workspace.");
  }

  const successResults = results.filter((r) => !r.error && resultsHaveDeliverablesInResult(r));
  const meetings: ResolvedClickUpMeeting[] = [];

  for (const result of successResults) {
    meetings.push(await resolveClickUpProjectForResult(teamId, lists, result));
  }

  return { teamId, meetings };
}

function resultsHaveDeliverablesInResult(result: AgentAnalysisResult): boolean {
  return parseDeliverables(result.parsed).length > 0;
}
